import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { getStoresByMultipleIdsString } from "../../shared/utils/getStores"

/**
 * Retrieves the product selection data (AHG) with their corresponding price list from a given customer
 *
 * @param {object} [args]
 * @param {string} [args.customerNumber] - the number of the customer
 * @param {string} [args.locale] - the locale
 * @returns {object<{items: array}>}
 */
export const queryCustomerProductSelections = {
  async customerProductSelections(_parent, args, _context, _info) {
    const { customerNumber, locale = AppSettings.defaultLocale() } = args

    const commerceApi = CommerceService.api()

    try {
      const customer = await getCustomer({ number: customerNumber })

      // only do this if customer was found
      if (!customer?.id) return null

      // retrieves the store keys from the given customer
      // filter out the keys that contain "priceList", "minimumSellingPrice", "purchasePrice" or "default"
      // returns a comma-separated string of the remaining keys
      const storeKeys = customer?.stores
        ?.filter(
          ({ key }) =>
            key.includes("priceList") ||
            key.includes("minimumSellingPrice") ||
            key.includes("purchasePrice") ||
            key.includes("default"),
        )
        ?.map(({ key }) => `"${key}"`)
        .join(",")

      if (!storeKeys?.length) return null

      // retrieves the store data based on the store keys from the given customer
      const storeDataByKeys = await getStoresByMultipleIdsString(storeKeys)

      if (!storeDataByKeys?.length) return null

      // returns an array of objects containing the store key and its product selection ID (AHG)
      const productSelectionIdByStore = storeDataByKeys.flatMap(({ key, productSelections }) =>
        productSelections
          ?.map(({ productSelection, active }) => {
            if (!active) return null
            return { key, id: productSelection?.id }
          })
          .filter((n) => n),
      )

      // returns a comma-separated string of the product selection IDs
      const productSelectionIds = productSelectionIdByStore?.map(({ id }) => `"${id}"`).join(",")

      // retrieves product selection data by their IDs
      const productSelectionDataByIds = (
        await commerceApi
          .productSelections()
          .get({
            queryArgs: {
              where: `id in (${productSelectionIds})`,
            },
          })
          .execute()
      )?.body?.results

      if (!productSelectionDataByIds?.length) return null

      // returns an array of objects with the key and name of the product selections (AHG) from the customer with their corresponding price list
      const productSelectionWithPriceList = productSelectionDataByIds
        .map(({ id, name, key }) => {
          const store = productSelectionIdByStore?.find((item) => item?.id === id)
          // product selections (AHG) with key "17" is no longer used
          // https://reply.atlassian.net/browse/EINZA-1261?focusedCommentId=51117
          if (key === "17") return null
          let priceList = "S"
          // extracts the price list number from the given store key
          // e.g. "VERBAND-26640-priceList3" => "3"
          if (store?.key?.match(/priceList(\d+)/)) {
            priceList = store?.key?.match(/priceList(\d+)/)[1]
          }
          if (store?.key?.includes("minimumSellingPrice")) {
            priceList = "M"
          }
          if (store?.key?.includes("purchasePrice")) {
            priceList = "E"
          }
          return { key, name: name?.[locale], priceList: priceList }
        })
        .filter((n) => n)

      if (!productSelectionWithPriceList?.length) return null

      return { items: productSelectionWithPriceList }
    } catch (err) {
      console.error(err)
      return null
    }
  },
}
